import { Controller } from "@hotwired/stimulus";

// Connects to data-controller="gallery-picker"
// Handles multi-select for gallery items (images, videos, assets)
export default class extends Controller {
  static targets = ["items", "input", "template", "preview"];
  static values = {
    fieldName: String,
    maxItems: { type: Number, default: 20 }
  };

  connect() {
    this.updatePreview();
  }

  // Method called from external JS (addToGallery function)
  addItem(item) {
    const items = this.getItems();
    
    // Check if already selected
    const existingIndex = items.findIndex(i => i.id === item.id);
    if (existingIndex !== -1) {
      // Already exists, don't add again
      return false;
    }
    
    // Check max items
    if (items.length >= this.maxItemsValue) {
      alert(`Maximum ${this.maxItemsValue} items allowed`);
      return false;
    }
    
    // Add new item
    items.push({
      type: item.type,
      url: item.url,
      id: item.id,
      thumbnail: item.thumbnail,
      filename: item.filename,
      width: item.width,
      height: item.height
    });
    
    this.setItems(items);
    this.updatePreview();
    return true;
  }

  select({ params: { type, url, id, thumbnail, filename, width, height } }) {
    const items = this.getItems();
    
    // Check if already selected
    const existingIndex = items.findIndex(item => item.id === id);
    if (existingIndex !== -1) {
      // Remove if already selected (toggle behavior)
      items.splice(existingIndex, 1);
    } else {
      // Check max items
      if (items.length >= this.maxItemsValue) {
        alert(`Maximum ${this.maxItemsValue} items allowed`);
        return;
      }
      // Add new item
      items.push({ type, url, id, thumbnail, filename, width, height });
    }
    
    this.setItems(items);
    this.updatePreview();
  }

  remove(event) {
    const index = parseInt(event.currentTarget.dataset.index, 10);
    const items = this.getItems();
    items.splice(index, 1);
    this.setItems(items);
    this.updatePreview();
  }

  moveUp(event) {
    const index = parseInt(event.currentTarget.dataset.index, 10);
    if (index === 0) return;
    
    const items = this.getItems();
    [items[index - 1], items[index]] = [items[index], items[index - 1]];
    this.setItems(items);
    this.updatePreview();
  }

  moveDown(event) {
    const index = parseInt(event.currentTarget.dataset.index, 10);
    const items = this.getItems();
    if (index >= items.length - 1) return;
    
    [items[index], items[index + 1]] = [items[index + 1], items[index]];
    this.setItems(items);
    this.updatePreview();
  }

  getItems() {
    try {
      return JSON.parse(this.inputTarget.value || "[]");
    } catch {
      return [];
    }
  }

  setItems(items) {
    this.inputTarget.value = JSON.stringify(items);
  }

  updatePreview() {
    const items = this.getItems();
    const previewContainer = this.previewTarget;
    previewContainer.innerHTML = "";

    if (items.length === 0) {
      previewContainer.innerHTML = '<p class="text-sm text-stone-500">No items selected</p>';
      return;
    }

    items.forEach((item, index) => {
      const itemEl = document.createElement("div");
      itemEl.className = "flex items-center space-x-2 p-2 bg-stone-50 rounded-md border border-stone-200";
      
      // Thumbnail or icon
      let thumbnailHtml = "";
      if (item.type === "video") {
        if (item.thumbnail) {
          thumbnailHtml = `<img src="${item.thumbnail}" class="w-16 h-12 object-cover rounded" />`;
        } else {
          thumbnailHtml = `<div class="w-16 h-12 bg-stone-200 rounded flex items-center justify-center">
            <svg class="w-6 h-6 text-stone-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>`;
        }
      } else if (item.thumbnail) {
        thumbnailHtml = `<img src="${item.thumbnail}" class="w-16 h-12 object-cover rounded" />`;
      } else if (item.url) {
        thumbnailHtml = `<img src="${item.url}" class="w-16 h-12 object-cover rounded" />`;
      } else {
        thumbnailHtml = `<div class="w-16 h-12 bg-stone-200 rounded flex items-center justify-center">
          <svg class="w-6 h-6 text-stone-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        </div>`;
      }

      itemEl.innerHTML = `
        <div class="flex-shrink-0">${thumbnailHtml}</div>
        <div class="flex-1 min-w-0">
          <p class="text-sm font-medium text-stone-900 truncate">${item.filename || item.id || "Item"}</p>
          <p class="text-xs text-stone-500">${item.type}${item.width && item.height ? ` - ${item.width}x${item.height}` : ""}</p>
        </div>
        <div class="flex items-center space-x-1">
          <button type="button" data-action="gallery-picker#moveUp" data-index="${index}" class="p-1 text-stone-500 hover:text-stone-700" ${index === 0 ? 'disabled' : ''}>
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 15l7-7 7 7" />
            </svg>
          </button>
          <button type="button" data-action="gallery-picker#moveDown" data-index="${index}" class="p-1 text-stone-500 hover:text-stone-700" ${index === items.length - 1 ? 'disabled' : ''}>
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          <button type="button" data-action="gallery-picker#remove" data-index="${index}" class="p-1 text-red-500 hover:text-red-700">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      `;
      
      previewContainer.appendChild(itemEl);
    });
  }

  isSelected(id) {
    return this.getItems().some(item => item.id === id);
  }
}
